import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel, OptioLogoFull } from './SharedComponents';

// ============ SLIDE 16: SALES HERO ============
export const SalesHeroSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-[#020206]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1600px] h-[1200px] bg-[#0068ff] opacity-[0.1] blur-[280px] rounded-full" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3d8bff] opacity-[0.05] blur-[150px] rounded-full" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#0052cc] opacity-[0.04] blur-[120px] rounded-full" />
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,104,255,0.3) 1px, transparent 0)', backgroundSize: '50px 50px' }} />
      <div className="absolute top-12 left-12 w-20 h-20"><div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#0068ff]/30 to-transparent" /><div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#0068ff]/30 to-transparent" /></div>
      <div className="absolute bottom-12 right-12 w-20 h-20"><div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#0068ff]/30 to-transparent" /><div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#0068ff]/30 to-transparent" /></div>
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <div className="mb-6 animate-fade-in"><OptioLogoFull /></div>
      <SectionLabel delay={0.1}>{slide.product}</SectionLabel>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center max-w-4xl mt-5 mb-4 leading-tight animate-slide-up">{slide.title}</h2>
      <p className="text-white/45 text-lg text-center max-w-2xl mb-3 animate-fade-in" style={{ animationDelay: '0.15s' }}>{slide.description}</p>
      <p className="text-white/35 text-base text-center max-w-2xl mb-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>{slide.tagline}</p>
      <GlassCard delay={0.3} className="px-6 py-2.5 mb-8" style={{ borderColor: '#0068ff30' }}>
        <span className="text-[#0068ff] font-semibold">{slide.callout}</span>
      </GlassCard>
      <a href="https://optioblockchain.com/shop" target="_blank" rel="noopener noreferrer" className="animate-scale-in no-underline" style={{ animationDelay: '0.4s' }}>
        <div className="px-10 py-4 rounded-xl bg-[#0068ff] text-white font-bold text-lg tracking-wide cursor-pointer hover:brightness-110 transition-all" style={{ boxShadow: '0 0 50px rgba(0,104,255,0.4)' }}>
          {slide.cta}
        </div>
      </a>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 17: AWARENESS ============
export const SalesAwarenessSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#080812] to-[#030308]" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#0068ff] opacity-[0.06] blur-[180px] rounded-full" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#f59e0b] opacity-[0.03] blur-[120px] rounded-full" />
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <SectionLabel color="#f59e0b" delay={0}>The Landscape</SectionLabel>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center max-w-4xl mt-5 mb-4 animate-slide-up">{slide.title}</h2>
      <p className="text-white/40 text-lg text-center max-w-2xl mb-10 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
      <GlassCard delay={0.2} className="px-8 py-6 max-w-2xl w-full" style={{ borderColor: 'rgba(245,158,11,0.15)' }}>
        <p className="text-white/35 text-sm mb-5">But most people still struggle to understand:</p>
        <div className="space-y-3">
          {slide.problems.map((p, i) => (
            <div key={i} className="flex items-center gap-3 animate-slide-up" style={{ animationDelay: `${0.25 + i * 0.08}s` }}>
              <span className="text-[#f59e0b] text-lg font-bold">?</span>
              <span className="text-white/60 text-base">{p}</span>
            </div>
          ))}
        </div>
      </GlassCard>
      <div className="mt-8 flex items-center gap-3 animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-white/15" />
        <span className="text-white font-semibold text-lg">{slide.punchline}</span>
        <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-white/15" />
      </div>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 18: CHALLENGE ============
export const SalesChallengeSlide = ({ slide }) => {
  const colors = ['#f43f5e', '#f59e0b', '#8b5cf6'];
  const icons = [
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
  ];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#0a0810] to-[#030308]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] bg-[#f43f5e] opacity-[0.05] blur-[180px] rounded-full" />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel color="#f43f5e" delay={0}>The Problem</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-3 animate-slide-up">{slide.title}</h2>
        <p className="text-white/40 text-lg mb-12 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
        <div className="grid grid-cols-3 gap-6 w-full max-w-4xl mb-10">
          {slide.concerns.map((c, idx) => (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="p-7 text-center" style={{ borderColor: `${colors[idx]}20`, background: `linear-gradient(180deg, ${colors[idx]}08, transparent)` }}>
              <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${colors[idx]}15`, color: colors[idx] }}>{icons[idx]}</div>
              <h3 className="text-white font-bold text-lg mb-2">{c.name}</h3>
              <p className="text-white/40 text-sm">{c.description}</p>
            </GlassCard>
          ))}
        </div>
        <GlassCard delay={0.6} className="px-8 py-3">
          <span className="text-white font-semibold text-lg">{slide.punchline}</span>
        </GlassCard>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 19: SOLUTION ============
export const SalesSolutionSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#040a14] to-[#030308]" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[1000px] h-[700px] bg-[#0068ff] opacity-[0.07] blur-[200px] rounded-full" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#22c55e] opacity-[0.04] blur-[120px] rounded-full" />
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <SectionLabel delay={0}>The Opportunity</SectionLabel>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-3 animate-slide-up">{slide.title}</h2>
      <p className="text-white/35 text-lg mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
      <GlassCard delay={0.2} className="px-10 py-5 mb-8" style={{ borderColor: '#0068ff40', boxShadow: '0 0 50px rgba(0,104,255,0.12)' }}>
        <span className="text-2xl md:text-3xl font-bold text-[#0068ff]">{slide.solution}</span>
      </GlassCard>
      <p className="text-white/40 text-base mb-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>{slide.description}</p>
      <div className="flex gap-4 mb-8">
        {slide.items.map((item, idx) => (
          <GlassCard key={idx} delay={0.35 + idx * 0.08} className="px-6 py-4 text-center" style={{ borderColor: '#0068ff20' }}>
            <svg viewBox="0 0 24 24" className="w-6 h-6 mx-auto mb-2 text-[#0068ff]" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
            <span className="text-white font-medium text-sm">{item}</span>
          </GlassCard>
        ))}
      </div>
      <GlassCard delay={0.7} className="px-6 py-2.5" style={{ borderColor: '#22c55e25' }}>
        <span className="text-[#22c55e] font-semibold">{slide.closing}</span>
      </GlassCard>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 20: COMPARISON ============
export const SalesComparisonSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0308] via-[#060610] to-[#030810]" />
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[600px] bg-[#6b7280] opacity-[0.05] blur-[150px] rounded-full" />
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[600px] bg-[#0068ff] opacity-[0.06] blur-[150px] rounded-full" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-white/[0.05] to-transparent" />
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center max-w-4xl mb-10 animate-slide-up">{slide.title}</h2>
      <div className="flex items-stretch gap-8 w-full max-w-4xl mb-8">
        <GlassCard delay={0.15} className="flex-1 p-8" style={{ borderColor: 'rgba(107,114,128,0.2)' }}>
          <div className="text-center mb-6">
            <svg viewBox="0 0 24 24" className="w-10 h-10 mx-auto mb-2 text-white/30" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
            <span className="text-xl font-bold text-white/60">{slide.left.label}</span>
          </div>
          <div className="space-y-3">
            {slide.left.rows.map((r, i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-white/[0.04]">
                <span className="text-white/35 text-sm">{r.metric}</span>
                <span className="text-white/60 text-sm font-medium">{r.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
        <div className="flex items-center"><div className="w-10 h-10 rounded-full bg-[#0068ff]/10 border border-[#0068ff]/25 flex items-center justify-center"><span className="text-[#0068ff] text-xs font-bold">VS</span></div></div>
        <GlassCard delay={0.25} className="flex-1 p-8" style={{ borderColor: '#0068ff30', boxShadow: '0 0 50px rgba(0,104,255,0.08)' }}>
          <div className="text-center mb-6">
            <svg viewBox="0 0 24 24" className="w-10 h-10 mx-auto mb-2 text-[#0068ff]" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>
            <span className="text-xl font-bold text-[#0068ff]">{slide.right.label}</span>
          </div>
          <div className="space-y-3">
            {slide.right.rows.map((r, i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-[#0068ff]/10">
                <span className="text-white/35 text-sm">{r.metric}</span>
                <span className="text-[#0068ff] text-sm font-bold">{r.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
      <GlassCard delay={0.5} className="px-8 py-3 text-center max-w-2xl">
        <span className="text-white/50 text-sm">{slide.closing}</span>
      </GlassCard>
    </div>
  </SlideWrapper>
);
